import { forwardRef } from "react";
import {
  Link as RouterLink,
  Navigate as RouterNavigate,
  useNavigate,
  type LinkProps,
} from "react-router";
import { useSitePath } from "./TemplateContext";

/**
 * SiteLink — drop-in replacement for React Router's <Link>.
 * Automatically prefixes absolute paths (e.g. "/about") with the
 * current template prefix ("/t/{templateId}").
 *
 * Relative paths (no leading "/") are passed through unchanged.
 */
export const Link = forwardRef<HTMLAnchorElement, LinkProps>(
  ({ to, ...props }, ref) => {
    const sitePath = useSitePath();
    const prefixedTo =
      typeof to === "string" && to.startsWith("/") ? sitePath(to) : to;
    return <RouterLink ref={ref} to={prefixedTo} {...props} />;
  },
);
Link.displayName = "SiteLink";

/**
 * SiteNavigate — drop-in replacement for React Router's <Navigate>.
 */
export function SiteNavigate({
  to,
  ...props
}: React.ComponentProps<typeof RouterNavigate>) {
  const sitePath = useSitePath();
  const prefixedTo =
    typeof to === "string" && to.startsWith("/") ? sitePath(to) : to;
  return <RouterNavigate to={prefixedTo} {...props} />;
}

/**
 * useSiteNavigate — drop-in replacement for React Router's useNavigate.
 */
export function useSiteNavigate() {
  const navigate = useNavigate();
  const sitePath = useSitePath();
  return (to: string | number, options?: Parameters<ReturnType<typeof useNavigate>>[1]) => {
    if (typeof to === "string" && to.startsWith("/")) {
      navigate(sitePath(to), options);
    } else {
      navigate(to as any, options);
    }
  };
}
